import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close,
  Warning,
  AccessTime,
  Person,
  Assignment,
  AttachFile,
} from '@mui/icons-material';
import { Incident } from '@models/incident.model';
import { useTranslation } from '@hooks/useTranslation';

interface AcknowledgeIncidentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  incident: Incident | null;
  isAcknowledging: boolean;
}

const AcknowledgeIncidentModal: React.FC<AcknowledgeIncidentModalProps> = ({
  open,
  onClose,
  onConfirm,
  incident,
  isAcknowledging,
}) => {
  // Sprint 28 change request (global sweep, defect fix). This top-level legacy component is a
  // separate, un-migrated sibling of the already-fixed `organisms/AcknowledgeIncidentModal` —
  // still live, rendered by `ActivityTab` inside the top-level `CreateEditTaskModal`
  // (`SchedulePage`/`ProjectsList`/`warehouse/equipment`). Per size discipline, only the
  // enum-rendering defect (status chip, incident type, delay unit) is fixed here — this
  // component has no other i18n wiring, and its remaining ~15 hardcoded labels ("Time of
  // Incident:", "Reported by:", button text, etc.) are a whole-component migration, sprint-29-
  // sized, deliberately left for now. Reuses `incidents.type*`/`incidents.unit*` and
  // `activityCards.incident.status*` — the same keys the organisms sibling already uses — instead
  // of forking a third set.
  const tIncidents = useTranslation('incidents');
  const tActivityCards = useTranslation('activityCards');
  const incidentTypeTextMap: Record<string, string> = {
    WEATHER: tIncidents.typeWeather,
    ACCIDENT: tIncidents.typeAccident,
    MATERIALS_ISSUE: tIncidents.typeMaterialsIssue,
    EQUIPMENT_ISSUE: tIncidents.typeEquipmentIssue,
    OTHER: tIncidents.typeOther,
  };
  const delayUnitTextMap: Record<string, string> = {
    HOURS: tIncidents.unitHours,
    DAYS: tIncidents.unitDays,
    WEEKS: tIncidents.unitWeeks,
  };
  const incidentStatusTextMap: Record<string, string> = {
    PENDING: tActivityCards.incident.statusPending,
    ACKNOWLEDGED: tActivityCards.incident.statusAcknowledged,
  };

  const formatTime = (dateInput: Date | string): string => {
    const date = new Date(dateInput);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const formatDate = (dateInput: Date | string): string => {
    const date = new Date(dateInput);
    const today = new Date();

    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    if (isToday) {
      return `Today at ${formatTime(dateInput)}`;
    }

    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()} at ${formatTime(dateInput)}`;
  };

  const getIncidentTypeDisplay = (
    type?: string,
    otherType?: string,
  ): string => {
    if (type === 'OTHER' && otherType) {
      return otherType;
    }
    if (!type) return tActivityCards.incident.unknownTypeFallback;
    return incidentTypeTextMap[type] || tActivityCards.incident.unknownTypeFallback;
  };

  if (!incident) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px',
          maxHeight: '90vh',
        },
      }}>
      <DialogTitle sx={{ m: 0, p: 2, paddingBottom: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: '#f59e0b', fontSize: '24px' }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Acknowledge Incident
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: '16px !important', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Incident Header */}
          <Box
            sx={{
              backgroundColor: '#fef2f2',
              padding: '20px',
              borderRadius: '8px',
              borderLeft: '4px solid #ef4444',
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 2,
              }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: '#111827', flex: 1 }}>
                {incident.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={incidentStatusTextMap[incident.status] || incident.status}
                  color="error"
                  size="small"
                  variant="filled"
                />
                {incident.incidentType && (
                  <Chip
                    label={getIncidentTypeDisplay(
                      incident.incidentType,
                      incident.otherIncidentType,
                    )}
                    size="small"
                    variant="outlined"
                    sx={{ color: '#6b7280', borderColor: '#d1d5db' }}
                  />
                )}
              </Box>
            </Box>

            {incident.description && (
              <Typography
                variant="body1"
                sx={{ color: '#374151', lineHeight: 1.6 }}>
                {incident.description}
              </Typography>
            )}
          </Box>

          {/* Incident Details */}
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
              Incident Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {incident.incidentTime && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime sx={{ fontSize: '18px', color: '#6b7280' }} />
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    <strong>Time of Incident:</strong>{' '}
                    {formatDate(incident.incidentTime)}
                  </Typography>
                </Box>
              )}

              {incident.delay && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime sx={{ fontSize: '18px', color: '#6b7280' }} />
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    <strong>Delay:</strong> {incident.delay}{' '}
                    {(incident.delayUnitOfMeasure &&
                      delayUnitTextMap[incident.delayUnitOfMeasure]) ||
                      incident.delayUnitOfMeasure?.toLowerCase()}
                  </Typography>
                </Box>
              )}

              {incident.tasks && incident.tasks.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Assignment
                    sx={{ fontSize: '18px', color: '#6b7280', mt: 0.2 }}
                  />
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    <strong>Affected Tasks:</strong>{' '}
                    {incident.tasks.map((task) => task.name).join(', ')}
                  </Typography>
                </Box>
              )}

              {incident.project && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment sx={{ fontSize: '18px', color: '#6b7280' }} />
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    <strong>Project:</strong> {incident.project.name}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person sx={{ fontSize: '18px', color: '#6b7280' }} />
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  <strong>Reported by:</strong> {incident.user?.firstName}{' '}
                  {incident.user?.lastName}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Documents Section */}
          {incident.documents && incident.documents.length > 0 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
                Attachments ({incident.documents.length})
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 2,
                }}>
                {incident.documents.map((document, index) => {
                  const isImage = document.name?.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i,
                  );
                  return (
                    <Box
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(document.url, '_blank');
                      }}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#3b82f6',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        },
                      }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}>
                        <AttachFile
                          sx={{ fontSize: '16px', color: '#6b7280' }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: '#374151' }}
                          noWrap>
                          {document.name || `Document ${index + 1}`}
                        </Typography>
                      </Box>
                      {isImage && (
                        <Box
                          sx={{
                            width: '100%',
                            height: '120px',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            backgroundColor: '#f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                          <img
                            src={document.url}
                            alt={document.name}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <Divider />

          {/* Acknowledgment Warning */}
          <Box
            sx={{
              backgroundColor: '#fef3cd',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #f59e0b',
            }}>
            <Typography
              variant="body2"
              sx={{ color: '#92400e', fontWeight: 500, mb: 1 }}>
              ⚠️ Acknowledge this incident?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#92400e', lineHeight: 1.5 }}>
              This action will mark the incident as acknowledged and record that
              you have reviewed this alert. Other users will be able to see that
              you have acknowledged this incident. This action cannot be undone.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#6b7280',
            borderColor: '#d1d5db',
            '&:hover': {
              backgroundColor: '#f9fafb',
              borderColor: '#9ca3af',
            },
          }}
          disabled={isAcknowledging}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={isAcknowledging}
          sx={{
            backgroundColor: '#f59e0b',
            '&:hover': {
              backgroundColor: '#d97706',
            },
            '&:disabled': {
              backgroundColor: '#d1d5db',
            },
          }}>
          {isAcknowledging ? 'Acknowledging...' : 'Acknowledge Incident'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcknowledgeIncidentModal;
